export const useDiversityResultsStore = defineStore('diversityResults', () => {
  // Map of botanical name (lowercase) to diversity information
  const diversityResults = ref(new Map<string, {
    originalName: string
    species: string
    genus: string
    family: string
    quantity: number
    relativeAbundance: number // For sequential colormap
    violatesSpeciesThreshold: boolean // For RAG colormap
    violatesGenusThreshold: boolean
    violatesFamilyThreshold: boolean
    simpsonsIndex: number
    totalPlants: number
    speciesCount: number
  }>())

  // Global diversity metrics for the current dataset
  const globalDiversityMetrics = ref({
    totalSpecies: 0,
    totalPlants: 0,
    simpsonsIndex: 0,
    genusDistribution: {} as Record<string, number>,
    familyDistribution: {} as Record<string, number>,
    speciesDistribution: {} as Record<string, number>,
    isValid: false,
    criteria: {
      minimumSpecies: { passed: false, message: '' },
      genusRule: { passed: false, message: '', violations: [] as string[] },
      familyRule: { passed: false, message: '', violations: [] as string[] },
      simpsonsRule: { passed: false, message: '', violations: [] as string[] }
    }
  })

  // Reactive getters
  const getDiversityResults = computed(() => diversityResults.value)
  const getGlobalMetrics = computed(() => globalDiversityMetrics.value)
  
  const getDiversityDataForName = (botanicalName: string) => {
    return diversityResults.value.get(botanicalName.toLowerCase()) || null
  }

  const hasDiversityResult = (botanicalName: string): boolean => {
    return diversityResults.value.has(botanicalName.toLowerCase())
  }

  // Get relative abundance for a species (0-1 scale)
  const getRelativeAbundance = (botanicalName: string): number | null => {
    const result = diversityResults.value.get(botanicalName.toLowerCase())
    return result?.relativeAbundance || null
  }

  // Get linearly scaled abundance that maps actual data range to full 0-1 colormap
  const getScaledAbundance = (botanicalName: string): number | null => {
    const result = diversityResults.value.get(botanicalName.toLowerCase())
    if (!result) return null

    // Get all abundance values to find min/max of actual data
    const allAbundances = Array.from(diversityResults.value.values())
      .map(r => r.relativeAbundance)

    if (allAbundances.length === 0) return null

    const minAbundance = Math.min(...allAbundances)
    const maxAbundance = Math.max(...allAbundances)

    // If all species have equal abundance, return middle value
    if (minAbundance === maxAbundance) return 0.5

    // Linear scaling: map [minAbundance, maxAbundance] to [0, 1]
    const scaledValue = (result.relativeAbundance - minAbundance) / (maxAbundance - minAbundance)
    
    return scaledValue
  }

  // Check if species violates any diversity thresholds (for RAG coloring)
  const violatesThresholds = (botanicalName: string): boolean => {
    const result = diversityResults.value.get(botanicalName.toLowerCase())
    if (!result) return false
    
    return result.violatesSpeciesThreshold || 
           result.violatesGenusThreshold || 
           result.violatesFamilyThreshold
  }

  // Actions - Update diversity results from validation data
  const updateDiversityResults = (plants: Array<{
    originalName: string
    species: string
    genus: string
    family: string
    quantity: number
    score: number
    source?: string
    matchType?: string
  }>, diversityMetrics: any) => {
    // Clear existing results
    diversityResults.value.clear()
    
    if (!plants.length) {
      console.log('[DiversityResultsStore] No plants provided, clearing results')
      return
    }

    // Calculate total plants and species distributions
    const totalPlants = plants.reduce((sum, plant) => sum + plant.quantity, 0)
    const speciesDistribution: Record<string, number> = {}
    const genusDistribution: Record<string, number> = {}
    const familyDistribution: Record<string, number> = {}

    // Calculate distributions
    plants.forEach(plant => {
      speciesDistribution[plant.species] = (speciesDistribution[plant.species] || 0) + plant.quantity
      genusDistribution[plant.genus] = (genusDistribution[plant.genus] || 0) + plant.quantity
      familyDistribution[plant.family] = (familyDistribution[plant.family] || 0) + plant.quantity
    })

    // Calculate thresholds (Santamour's 10-20-30 rule)
    const speciesThreshold = 0.1  // 10% for species
    const genusThreshold = 0.2    // 20% for genus
    const familyThreshold = 0.3   // 30% for family

    // Process each plant and determine thresholds
    plants.forEach(plant => {
      const speciesTotal = speciesDistribution[plant.species]
      const genusTotal = genusDistribution[plant.genus]
      const familyTotal = familyDistribution[plant.family]
      
      const relativeAbundance = speciesTotal / totalPlants
      const genusPercent = genusTotal / totalPlants
      const familyPercent = familyTotal / totalPlants

      // Check threshold violations
      const violatesSpeciesThreshold = relativeAbundance > speciesThreshold
      const violatesGenusThreshold = genusPercent > genusThreshold
      const violatesFamilyThreshold = familyPercent > familyThreshold

      // Debug logging for threshold violations
      if (violatesSpeciesThreshold || violatesGenusThreshold || violatesFamilyThreshold) {
        console.log(`[DiversityResultsStore] SANTAMOUR'S 10-20-30 RULE VIOLATION - ${plant.originalName}:`, {
          species: plant.species,
          relativeAbundance: `${(relativeAbundance * 100).toFixed(1)}%`,
          genusPercent: `${(genusPercent * 100).toFixed(1)}%`,
          familyPercent: `${(familyPercent * 100).toFixed(1)}%`,
          violatesSpecies: violatesSpeciesThreshold,
          violatesGenus: violatesGenusThreshold,
          violatesFamily: violatesFamilyThreshold
        })
      }

      diversityResults.value.set(plant.originalName.toLowerCase(), {
        originalName: plant.originalName,
        species: plant.species,
        genus: plant.genus,
        family: plant.family,
        quantity: plant.quantity,
        relativeAbundance,
        violatesSpeciesThreshold,
        violatesGenusThreshold,
        violatesFamilyThreshold,
        simpsonsIndex: diversityMetrics?.simpsonsIndex || 0,
        totalPlants,
        speciesCount: Object.keys(speciesDistribution).length
      })
    })

    // Update global metrics
    globalDiversityMetrics.value = {
      totalSpecies: Object.keys(speciesDistribution).length,
      totalPlants,
      simpsonsIndex: diversityMetrics?.simpsonsIndex || 0,
      genusDistribution,
      familyDistribution,
      speciesDistribution,
      isValid: diversityMetrics?.isValid || false,
      criteria: diversityMetrics?.criteria || {
        minimumSpecies: { passed: false, message: '' },
        genusRule: { passed: false, message: '', violations: [] },
        familyRule: { passed: false, message: '', violations: [] },
        simpsonsRule: { passed: false, message: '', violations: [] }
      }
    }

    console.log(`[DiversityResultsStore] Updated diversity results for ${plants.length} plants`)
    console.log('[DiversityResultsStore] Global metrics:', globalDiversityMetrics.value)
    
    // Log abundance distribution for debugging color mapping
    const allAbundanceValues = Array.from(diversityResults.value.values()).map(r => r.relativeAbundance)
    const minAbundance = Math.min(...allAbundanceValues)
    const maxAbundance = Math.max(...allAbundanceValues)
    
    const abundances = Array.from(diversityResults.value.values())
      .map(r => ({ 
        species: r.species, 
        relativeAbundance: r.relativeAbundance,
        scaledAbundance: allAbundanceValues.length > 0 && minAbundance !== maxAbundance 
          ? (r.relativeAbundance - minAbundance) / (maxAbundance - minAbundance)
          : 0.5
      }))
      .sort((a, b) => b.relativeAbundance - a.relativeAbundance)
      .slice(0, 5) // Top 5 most abundant
    
    console.log('[DiversityResultsStore] Top 5 species with scaling:', abundances)
    console.log('[DiversityResultsStore] Data range scaling:', {
      originalMin: minAbundance,
      originalMax: maxAbundance,
      scalingRange: `${minAbundance.toFixed(3)} → 0.0, ${maxAbundance.toFixed(3)} → 1.0`,
      fullColormapUtilization: minAbundance !== maxAbundance
    })
    
    // Summary of threshold violations for RAG coloring
    const violationSummary = Array.from(diversityResults.value.values()).reduce((acc, result) => {
      if (result.violatesSpeciesThreshold) acc.species++
      if (result.violatesGenusThreshold) acc.genus++
      if (result.violatesFamilyThreshold) acc.family++
      if (result.violatesSpeciesThreshold || result.violatesGenusThreshold || result.violatesFamilyThreshold) {
        acc.anyViolation++
      }
      return acc
    }, { species: 0, genus: 0, family: 0, anyViolation: 0, total: Array.from(diversityResults.value.values()).length })
    
    console.log('[DiversityResultsStore] Threshold violation summary (20% rule):', violationSummary)
  }

  const clearDiversityResults = () => {
    diversityResults.value.clear()
    globalDiversityMetrics.value = {
      totalSpecies: 0,
      totalPlants: 0,
      simpsonsIndex: 0,
      genusDistribution: {},
      familyDistribution: {},
      speciesDistribution: {},
      isValid: false,
      criteria: {
        minimumSpecies: { passed: false, message: '' },
        genusRule: { passed: false, message: '', violations: [] },
        familyRule: { passed: false, message: '', violations: [] },
        simpsonsRule: { passed: false, message: '', violations: [] }
      }
    }
    console.log('[DiversityResultsStore] Cleared all diversity results')
  }

  return {
    // State
    diversityResults: readonly(diversityResults),
    globalDiversityMetrics: readonly(globalDiversityMetrics),
    
    // Getters
    getDiversityResults,
    getGlobalMetrics,
    
    // Actions
    getDiversityDataForName,
    hasDiversityResult,
    getRelativeAbundance,
    getScaledAbundance,
    violatesThresholds,
    updateDiversityResults,
    clearDiversityResults
  }
}) 